"use client";
import { IResource } from "@gettactic/api";
import { useMap } from "@/features/map/MapProvider";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { MapViewEmbedded } from "@/features/map/components/MapView/MapViewEmbedded";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { cn } from "@gettactic/helpers";

export const MapWithMiniOfficePhoto = ({
  resourceId,
  officeId,
  parentId,
  startDate,
  resourceImage,
  showMiniMap,
  className,
  addMainClassName = true
}: {
  resourceId: string;
  officeId: string;
  parentId: string;
  startDate: string;
  resourceImage: string | undefined;
  showMiniMap?: boolean;
  className?: string;
  addMainClassName?: boolean;
}) => {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const controlsContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cn(
        addMainClassName &&
          "relative h-[220px] overflow-hidden border-1 border-neutral-100 bg-neutral-50 md:h-[324px] md:rounded-sm",
        className
      )}
      // Include parentId so switching to a reservation on a different floor
      // of the same office forces a remount, not just a prop update.
      key={`${resourceId}-${parentId}`}
    >
      {resourceImage && (
        <div className="absolute bottom-[8px] left-[8px] z-10 h-[75px] w-[110px] overflow-hidden rounded-lg bg-neutral-50">
          <Image
            src={resourceImage}
            alt="Office Photo"
            layout="fill"
            className="rounded-lg object-cover"
          />
        </div>
      )}
      <MapViewEmbedded
        officeId={officeId}
        floorId={parentId}
        focussedResourceId={resourceId}
        date={startDate}
        enableControls={isMd}
        showMiniMap={showMiniMap}
        controlsPortalTarget={controlsContainerRef.current}
      />
      {/* Container for map controls (minimap and zoom) */}
      <div
        ref={controlsContainerRef}
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 20 }}
      >
        {/* Controls will be portaled here */}
      </div>
    </div>
  );
};

export const MapWithProvider = ({
  resourceId,
  children
}: {
  resourceId: string;
  children: React.ReactNode;
}) => {
  const { events } = useMap();

  useEffect(() => {
    events.trigger(
      "onHighlightUserResource",
      // we only really need an id
      [{ resource: resourceId }] as unknown as IResource[]
    );
  }, [resourceId]);

  return resourceId ? children : null;
};
